import React, { useEffect, useState } from 'react';

import { useSnackbar } from 'notistack';

import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField } from '@mui/material';

import useCurrentTenant from '~/hooks/useCurrentTenant';

import { PinnedOffer, createPinnedOffer, updatePinnedOffer } from '~/services/PublicOfferFeedService';

type Props = {
  open: boolean;
  setOpen: (open: boolean) => void;
  fetchData: () => void;
  editableOffer: PinnedOffer | null;
};

export default function PinnedOffersForm({ open, setOpen, fetchData, editableOffer }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const tenant = useCurrentTenant();

  // Form state
  const [offerId, setOfferId] = useState<string>('');
  const [minLengthOfStay, setMinLengthOfStay] = useState<number>(0);
  const [id, setId] = useState<string>('');

  // Form validation
  const [errors, setErrors] = useState<{
    region?: string;
    offerId?: string;
  }>({});

  const isEditMode = !!editableOffer;

  // Reset form when dialog opens/closes
  useEffect(() => {
    if (open) {
      if (editableOffer) {
        // Edit mode - populate with existing data
        setOfferId(editableOffer.offer_id);
        setMinLengthOfStay(editableOffer.min_length_of_stay || 0);
        setId(editableOffer.id);
      } else {
        // Create mode - reset form
        setOfferId('');
        setMinLengthOfStay(0);
      }
      setErrors({});
    }
  }, [open, editableOffer]);

  const validateForm = () => {
    const newErrors: { region?: string; offerId?: string } = {};

    if (!offerId.trim()) {
      newErrors.offerId = 'Offer ID is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    if (!offerId.trim()) {
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        offer_id: offerId.trim(),
        ...(minLengthOfStay && typeof minLengthOfStay === 'number' && { min_length_of_stay: minLengthOfStay }),
        brand: tenant.tenant.brand,
      };

      if (isEditMode) {
        await updatePinnedOffer(
          {
            min_length_of_stay: minLengthOfStay,
            brand: tenant.tenant.brand,
          },
          id,
        );
        enqueueSnackbar('Pinned offer updated successfully', { variant: 'success' });
      } else {
        await createPinnedOffer(payload);
        enqueueSnackbar('Pinned offer created successfully', { variant: 'success' });
      }

      setOpen(false);
      fetchData();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'An error occurred';
      enqueueSnackbar(`Failed to ${isEditMode ? 'update' : 'create'} pinned offer: ${errorMessage}`, {
        variant: 'error',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setOpen(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEditMode ? 'Edit' : 'Create'} Pinned Offer</DialogTitle>
      <DialogContent>
        <Box mt={2}>
          <Stack spacing={3}>
            <TextField
              label="Offer ID"
              value={offerId}
              onChange={(e) => {
                setOfferId(e.target.value);
                if (errors.offerId && e.target.value.trim()) {
                  setErrors({ ...errors, offerId: undefined });
                }
              }}
              disabled={isEditMode} // Offer ID cannot be changed in edit mode
              error={!!errors.offerId}
              helperText={errors.offerId || 'Enter the offer ID'}
              fullWidth
              required
            />
            <TextField
              label="Min Length of Stay"
              type="number"
              value={minLengthOfStay}
              onChange={(e) => {
                const value = parseInt(e.target.value, 10);
                setMinLengthOfStay(value);
              }}
              helperText="Optional: Minimum length of stay in days"
              fullWidth
              inputProps={{ min: 1 }}
            />
          </Stack>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isSubmitting} variant="text">
          Cancel
        </Button>
        <Button onClick={handleSubmit} disabled={isSubmitting} variant="contained" color="primary">
          {isSubmitting ? (isEditMode ? 'Updating...' : 'Creating...') : isEditMode ? 'Update' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
